import React, { useEffect, useState } from 'react';
import styles from './LastFiveYearsPortfolioOverview.module.scss';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Indicator from '../../../models/Indicator';
import { capitalizeFirstLetter, translateMultipleText } from '../../../services/stringUtil';
import { isMobile } from '../../../services/booleanUtil';
import { BarChart } from '../../Portfolio/CardIndicator/Cards/BarChart/BarChart';
import { getProjectsByDate, resetIndicatorProjects } from '../../../redux/actions/indicatorProjects';
import Table from './Table';
import { PORTFOLIO_OVERVIEW_LAST_5_YEARS_SELECTED_YEAR } from '../../../services/sessionFiltersIndicatorDetailHelper';
import IndicatorDetailFilter from '../../../models/IndicatorDetailFilter';
import { getNewFilteredIndicators } from '../../../api/indicatorApi';
import { processIndicators } from '../../Portfolio/indicatorHelper';
import { animateScroll } from 'react-scroll';
import SpinningLoader from '../../ProjectDetail/ProjectTabs/Loader/SpinningLoader';
import FilterIndicators from '../../../models/FilterIndicators';
import { AppMenuTabs } from '../../MenuTabs/userTabsInterface';
import { setIndicatorResponse, setLoading } from '../../../redux/actions/dataTable';
import { emptyProjects } from '../../Portfolio/PortfolioUtil';
import { ProjectProductTypes } from '../../../models/ProjectProductTypes';
import { arrayMove } from 'react-sortable-hoc';
import { addIndicators } from '../../../redux/actions/indicators';
import { setCurrentIndicator } from '../../../redux/actions/currentIndicator';
import { updateCreatingOrEditing } from '../../../redux/actions/menuTabs';
import Filter from '../../Portfolio/Filter/Filter';
import ErrorMessageView from '../../Util/ErrorMessage/ErrorMessageView';

const baseColor = 'rgba(0, 207, 221, 1)';
const lastYearColor = 'rgba(253, 172, 65, 1)';
const fontsColor = '#727e8c';
const gridLinesColor = '#F2F3F7';
const graphFontFamily = 'Source Sans Pro';
const amountColor = '#416DBC';
const projectColors = '#727E8C';

const updateChartColors = (selectedYear: string, labels: string[]) => {
  const colorsList = new Array(labels?.length).fill(baseColor);
  colorsList[colorsList?.length - 1] = lastYearColor;
  const newColorIndex = labels?.indexOf(selectedYear);
  if (newColorIndex > -1) {
    colorsList[newColorIndex] = colorsList[newColorIndex].replace('1)', '0.4)');
  }
  return colorsList;
};

const basicDataLabel = t => {
  return {
    labels: {
      amount: {
        formatter: (value, ctx) => {
          return `${Math.round(value)} MM`;
        },
        anchor: 'end',
        clamp: true,
        align: 'start',
        offset: -50,
        padding: 1,
        backgroundColor: 'transparent',
        color: amountColor,
        clip: false,
        font: {
          size: 16,
          family: graphFontFamily,
          weight: 'bold',
        },
      },
      projects: {
        formatter: (value, ctx) => {
          const totalProjects = ctx.dataset.projQtyData[ctx.dataIndex];
          const label = totalProjects > 1 ? t('projects') : t('project');
          return `${totalProjects} ${capitalizeFirstLetter(label)}`;
        },
        anchor: 'end',
        clamp: true,
        align: 'start',
        offset: -25,
        padding: 1,
        backgroundColor: 'transparent',
        color: projectColors,
        clip: false,
        font: {
          size: 14,
          family: graphFontFamily,
        },
      },
    },
  };
};

export const dataForDetail = (data, t) => {
  return {
    ...data,
    datasets: data?.datasets.map(i => {
      i.barThickness = isMobile() ? 30 : 80;
      i.datalabels = basicDataLabel(t);
      return i;
    }),
  };
};

export const optionsforDetail = (t: Function, suggestedMax: number) => ({
  legend: {
    display: false,
  },
  scales: {
    yAxes: [
      {
        display: true,
        gridLines: {
          display: true,
          borderDash: [2, 2],
          color: gridLinesColor,
          drawBorder: false,
          zeroLineColor: gridLinesColor,
        },
        ticks: {
          suggestedMax,
          beginAtZero: true,
          fontSize: 10,
          fontFamily: graphFontFamily,
          fontColor: fontsColor,
          callback: (value, ctx) => {
            return `${value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`;
          },
        },
        scaleLabel: {
          display: true,
          labelString: `${t('millions_word_disbursement_projection_last_5_years')} ${t('dolar_sign')}`,
          fontSize: 10,
          fontFamily: graphFontFamily,
          fontColor: fontsColor,
        },
      },
    ],
    xAxes: [
      {
        display: true,
        gridLines: {
          display: false,
        },
        ticks: {
          fontColor: projectColors,
          fontSize: 14,
        },
      },
    ],
  },
  cornerRadius: 3,
  tooltips: {
    enabled: false,
  },
});

const handleSetChartData = (chartData, newBackgroundColors) => {
  return {
    ...chartData,
    datasets: chartData?.datasets?.map(i => {
      if (i.label === 'bar') {
        i.backgroundColor = newBackgroundColors;
        return i;
      } else {
        return i;
      }
    }),
  };
};

const LastFiveYearsPortfolioOverview = () => {
  const { t } = useTranslation();
  const { projectTypeFilterOnDetail } = useSelector<any, IndicatorDetailFilter>(state => state.indicatorDetailFilter);
  const currentIndicator = useSelector<any, Indicator>(state => state.currentIndicator);

  const { filterIndicators } = useSelector<any, any>(state => state);

  const [chartData, setChartData] = useState<any>(null);

  const [selectedYear, setSelectedYear] = useState<string>(
    sessionStorage.getItem(PORTFOLIO_OVERVIEW_LAST_5_YEARS_SELECTED_YEAR) || '',
  );
  const [yearFilter, setYearFilter] = useState<boolean>(false);
  const [handleYearOnPage, setHandleYearOnPage] = useState(false);
  const { chartContainer } = styles;
  const dispatch = useDispatch();

  // Filter
  const filterIndicatorsBar = useSelector<any, FilterIndicators>(state => state.filterIndicators);
  const stateProjectProductTypes = useSelector<any, FilterIndicators>(
    state => state.indicatorPayload.payload.projectProductTypes,
  );
  const menuTabs = useSelector<any, AppMenuTabs>(state => state.menuTabs);
  const portfolioSelectedTab = menuTabs.portfolioSelectedTab;
  const creatingOrEditing = menuTabs.creatingOrEditing;
  const [isLoading, setIsLoading] = React.useState(false);
  const [totalProjectsData, setTotalProjectsData] = React.useState(false);
  const [flag, setFlag] = useState<boolean>(false);
  const [hasError, setHasError] = useState(false);

  const projectTypeFilterChange = {
    ...filterIndicatorsBar,
    common: { ...filterIndicatorsBar.common, projectTypeFilters: stateProjectProductTypes },
    projectTypeFilters: stateProjectProductTypes,
  };

  const handleCommonFilterChange = async () => {
    dispatch(setLoading(true));
    setIsLoading(true);
    setHasError(false);

    try {
      const indicatorsToProcess = await getNewFilteredIndicators(
        {},
        { projectProductTypes: stateProjectProductTypes },
        {
          ...projectTypeFilterChange.common,
        },
      );
      dispatch(setIndicatorResponse(indicatorsToProcess));
      startProcessIndicators(
        indicatorsToProcess,
        translateMultipleText(` ${' '} ${portfolioSelectedTab.title}`, t),
        filterIndicatorsBar.common.projectTypeFilters,
      );
      setTotalProjectsData(emptyProjects(indicatorsToProcess.totalProjects));
    } catch (err) {
      setHasError(true);
      console.error(err, 'Error fetching data from indicators');
    } finally {
      dispatch(setLoading(false));
      setIsLoading(false);
    }
  };

  const startProcessIndicators = (inds, titleFromTab, projectTypeFilter = [ProjectProductTypes.Loan]) => {
    const result = processIndicators(inds, {
      titleFromTab,
      projectTypeFilter,
    });
    const elementSummary: any = result.find(elem => elem.id === 15);
    const indexSummary: number = result.indexOf(elementSummary);
    const sendEndSummary = arrayMove(result, indexSummary, result.length);

    dispatch(addIndicators(sendEndSummary));
    dispatch(
      setCurrentIndicator(sendEndSummary?.find(data => data.indicatorKey === 'PORTFOLIO_OVERVIEW_LAST_5_YEARS')),
    );
    setIsLoading(false);
  };


  const getBarChartData = async () => {
    setHasError(false);
    try {
      const options = { indicatorId: currentIndicator.id };
      const currentIndicatorOnDetail = await getNewFilteredIndicators(
        options,
        { projectProductTypes: stateProjectProductTypes },
        { ...projectTypeFilterChange.common },
      );
      const justCurrentIndicatorOnDetail = {
        ...currentIndicatorOnDetail,
        indicatorsData: currentIndicatorOnDetail.indicatorsData.filter(i => i.id === currentIndicator.id),
      };
      const indicatorsProcessed = processIndicators(justCurrentIndicatorOnDetail);
      const data = dataForDetail(indicatorsProcessed[0]?.data, t);
      if (handleYearOnPage) {
        setChartData(data);
      } else {
        const newBackgroundColors = updateChartColors(selectedYear, data.labels);
        setChartData(handleSetChartData(data, newBackgroundColors));
      }
    } catch (err) {
      setHasError(true);
      console.error(err, 'Error fetching last five years portfolio overview data');
    }
  };
  const handleClickBar = (year = '') => {
    setYearFilter(prev => !prev);
    setHandleYearOnPage(true);
    year === selectedYear ? setSelectedYear('2020') : setSelectedYear(year);
    sessionStorage.setItem(PORTFOLIO_OVERVIEW_LAST_5_YEARS_SELECTED_YEAR, year);
    sessionStorage.removeItem('PaginationIndicator');
    setTimeout(() => {
      animateScroll.scrollToBottom({ smooth: true, duration: 800 });
    }, 50);
  };

  useEffect(() => {
    selectedYear && !handleYearOnPage && setHandleYearOnPage(true);
    selectedYear && dispatch(resetIndicatorProjects());
    getBarChartData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectTypeFilterOnDetail, filterIndicatorsBar.common]);

  useEffect(() => {
    if (selectedYear && chartData) {
      dispatch(getProjectsByDate(6, parseInt(selectedYear), currentIndicator.id, undefined));
      const newBackgroundColors = updateChartColors(selectedYear, chartData.labels);
      setChartData(handleSetChartData(chartData, newBackgroundColors));
    } else if (selectedYear && chartData === null) {
      dispatch(getProjectsByDate(6, parseInt(selectedYear), currentIndicator.id, undefined));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, filterIndicators]);

  useEffect(() => {
    if (
      filterIndicatorsBar.common.coReponsibleDivisions.length > 0 ||
      filterIndicatorsBar.common.countryId.length > 0 ||
      filterIndicatorsBar.common.divisionId.length > 0 ||
      filterIndicatorsBar.common.interestIssues.length > 0 ||
      filterIndicatorsBar.common.participantDivisions.length > 0 ||
      filterIndicatorsBar.common.regionId.length > 0 ||
      filterIndicatorsBar.common.sectors.length > 0 ||
      filterIndicatorsBar.common.teamLeaders.length > 0
    )
      handleCommonFilterChange();
    setFlag(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterIndicatorsBar.common]);

  useEffect(() => {
    // dispatch(resetIndicatorDetailFilter());
    if (
      !menuTabs.firstLoad &&
      // tabId0OrCreatingEditing(menuTabs, creatingOrEditing) &&
      filterIndicatorsBar.common.coReponsibleDivisions.length === 0 &&
      filterIndicatorsBar.common.countryId.length === 0 &&
      filterIndicatorsBar.common.divisionId.length === 0 &&
      filterIndicatorsBar.common.interestIssues.length === 0 &&
      filterIndicatorsBar.common.participantDivisions.length === 0 &&
      filterIndicatorsBar.common.regionId.length === 0 &&
      filterIndicatorsBar.common.sectors.length === 0 &&
      filterIndicatorsBar.common.teamLeaders.length === 0 &&
      flag
    ) {
      handleCommonFilterChange();
      setFlag(false);
      if (creatingOrEditing) {
        dispatch(updateCreatingOrEditing(false));
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterIndicatorsBar.common]);

  return (
    <>
      {!hasError ? (
        <>
          <section className={chartContainer}>
            {chartData && projectTypeFilterOnDetail !== null ? (
              <BarChart
                type={currentIndicator.type}
                data={chartData}
                showSubLabels={!isMobile()}
                listPage={true}
                datasetClick={e => handleClickBar(e.label)}
                indicatorKey={currentIndicator.indicatorKey}
                chartOptions={optionsforDetail(t, chartData.suggestedMax)}
                chartDimensions={{
                  mobile: { width: 300, height: 200 },
                  desktop: { width: 800, height: 400 },
                }}
              />
            ) : (
              <SpinningLoader />
            )}
          </section>
          {selectedYear !== '' && <Table yearFilter={yearFilter} />}
        </>
      ) : (
        <div className="error-message-style">
          <ErrorMessageView
            title={t('title_error_message_view')}
            description={t('desc_error_message_tab')}
            reload={handleCommonFilterChange}
            isIndicator={true}
          />
        </div>
      )}
    </>
  );
};

export default LastFiveYearsPortfolioOverview;
